import {
  Container,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import NavigationHeader from "../../../components/NavigationHeader";
import { GlobalLabels, PROJECT_TERMINOLOGY, ProjectTabEnum } from "../enums";
import {
  smallButtonProp,
  tableCellStyles,
  tableHeaderStyles,
} from "../../../utils/styles";
import { useToast } from "../../../components/Toast";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  projectLogApiService,
  superProjectLogApiService,
} from "../services/projectLogApiService";
import { Loading } from "../../../components/Loading";
import {
  formatDate,
  formatText,
  formatTime12Hour,
  formatVehicleNumber,
} from "../../../utils/utils";
import DocumentStackDisplay from "../components/DocumentStackDisplay";
import { UploadedFile } from "../../../components/FileUploadSection";
import { DirectionsBus } from "@mui/icons-material";
import { OperationType } from "../../../types/operationType";
import { usePlanStatus } from "../../../hooks/usePlanStatus";
import { ProjectDetailTabs } from "../types";

// Type for project log data
interface ProjectLog {
  id: string;
  projectId: string;
  routeId: string | null;
  scheduleId: string | null;
  vehicleId: string | null;
  startLocation: string | null;
  endLocation: string | null;
  startTime: string;
  endTime: string;
  startOdometer: number | null;
  endOdometer: number | null;
  dateOfEntry: string;
  occupancy: number;
  notes?: string;
  documents?: UploadedFile[];
  staffAttendances: Array<{
    id: string;
    staffId: string;
    attendanceStatus: string;
    staff?: {
      id: string;
      name: string;
      type: string;
      contactNumber: string;
      status: string;
    };
  }>;
  vehicle: {
    id: string;
    vehicleNumber: string;
    vehicleType: string;
    model: string;
    status: string;
  };
  route: {
    id: string;
    fromLocation: string;
    toLocation: string;
    routeManagerName: string;
    routeManagerNumber: string;
  };
  schedule: {
    id: string;
    name: string;
    status: string;
  };
  project: {
    id: string;
    name: string;
    description: string;
    status: string;
  };
  createdByUser: {
    id: string;
    name: string;
    email: string;
    role: string;
  };
}

export const renderBasicLogInfoByVehicle = (vehicle: any) => {
  return (
    <Paper sx={{ p: 1 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <DirectionsBus sx={{ fontSize: 20, color: "#000" }} />
          <Typography variant="h6" color="primary" sx={{ fontSize: "0.8rem" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {formatVehicleNumber(vehicle?.vehicleNumber)}
            </Box>
          </Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <span style={{ fontSize: "0.75rem", color: "black" }}>
            {vehicle?.model} ({vehicle?.makeYear})
          </span>
          <span style={{ fontSize: "0.75rem", color: "black" }}>
            {vehicle?.fuelType}
          </span>
          <span style={{ fontSize: "0.75rem", color: "black" }}>
            {vehicle?.configuration}
          </span>
        </Box>
      </Box>
    </Paper>
  );
};

const ProjectLogsByVehicle = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [projectLogs, setProjectLogs] = useState<ProjectLog[]>([]);
  const [logInfo, setLogInfo] = useState<any | null>(null);
  const { showToast } = useToast();
  const { id: projectId, superProjectId } = useParams();
  const [searchParams] = useSearchParams();
  const vehicleId = searchParams.get("vehicleId");
  const allocationId = searchParams.get("allocationId");
  const navigate = useNavigate();
  const { planStatus } = usePlanStatus();
  const fetchProjectLogs = async () => {
    try {
      setIsLoading(true);
      const [logs, logInfo] = await Promise.all([
        superProjectLogApiService.getDataForSuperProjectViewLog(
          projectId!,
          vehicleId!
        ),
        superProjectLogApiService.getDataForSuperProjectLog(
          projectId!,
          vehicleId!
        ),
      ]);
      setProjectLogs(logs);
      setLogInfo(logInfo);
    } catch (error) {
      showToast("Error fetching project logs", "error");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (projectId && vehicleId) {
      fetchProjectLogs();
    }
  }, [projectId, vehicleId]);

  const handleAddLog = () => {
    if (
      planStatus?.operationType === OperationType.LARGE_FLEET ||
      planStatus?.operationType === OperationType.LARGE_FLEET_ALL
    ) {
      if (superProjectId) {
        navigate(
          `/super/${superProjectId}/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}/logs/new/by-vehicle?vehicleId=${vehicleId}&allocationId=${allocationId}`
        );
      } else {
        navigate(
          `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}/logs/new/by-vehicle?vehicleId=${vehicleId}&allocationId=${allocationId}`
        );
      }
    } else {
      if (superProjectId) {
        navigate(
          `/super/${superProjectId}/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}/logs/new?vehicleId=${vehicleId}&allocationId=${allocationId}`
        );
      } else {
        navigate(
          `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}/logs/new?vehicleId=${vehicleId}&allocationId=${allocationId}`
        );
      }
    }
  };

  return (
    <Container sx={{ my: 2 }}>
      <NavigationHeader
        title={`${ProjectTabEnum.LOGS.label}`}
        breadcrumbs={[
          {
            label: PROJECT_TERMINOLOGY.PLURAL,
            path: superProjectId
              ? `/super`
              : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}`,
          },
          ...(superProjectId
            ? [
                {
                  label: ProjectTabEnum.DIVISIONS.label,
                  path: `/super/${superProjectId}?isSuperProject=true&tab=${ProjectDetailTabs.DIVISIONS}`,
                },
                {
                  label: `${ProjectTabEnum.DIVISIONS.label} Details`,
                  path: `/super/${superProjectId}/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}`,
                },
              ]
            : []),
          {
            label: ProjectTabEnum.LOGS.label,
            path: superProjectId
              ? `/super/${superProjectId}/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}?tab=${ProjectDetailTabs.LOGS}`
              : `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${projectId}?tab=${ProjectDetailTabs.LOGS}`,
          },
          {
            label: `${ProjectTabEnum.LOGS.label}`,
          },
        ]}
        button={{
          label: "Add Log",
          onClick: handleAddLog,
        }}
      />

      {isLoading ? (
        <Loading message="Loading project logs..." />
      ) : (
        <>
          {logInfo && renderBasicLogInfoByVehicle(logInfo?.vehicle)}
          <TableContainer component={Paper} sx={{ mt: 2, boxShadow: 2 }}>
            <Table size="small" aria-label="project logs table">
              <TableHead>
                <TableRow>
                  <TableCell sx={tableHeaderStyles}>Date</TableCell>
                  <TableCell sx={tableHeaderStyles}>Time</TableCell>
                  <TableCell sx={tableHeaderStyles}>Staff Attendance</TableCell>
                  <TableCell sx={tableHeaderStyles}>Documents</TableCell>
                  <TableCell sx={tableHeaderStyles}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projectLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No project logs found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  projectLogs.map((log) => {
                    const handleEditLog = () => {
                      if (superProjectId) {
                        navigate(
                          `/super/${superProjectId}/${
                            PROJECT_TERMINOLOGY.LOWER_PLURAL
                          }/${log?.projectId}/logs/edit?routeId=${
                            log.routeId || ""
                          }&scheduleId=${log?.scheduleId || ""}&vehicleId=${
                            log?.vehicleId || ""
                          }&logId=${log.id}`
                        );
                      } else {
                        navigate(
                          `/${PROJECT_TERMINOLOGY.LOWER_PLURAL}/${
                            log?.projectId
                          }/logs/edit?routeId=${log.routeId || ""}&scheduleId=${
                            log?.scheduleId || ""
                          }&vehicleId=${log?.vehicleId || ""}&logId=${log.id}`
                        );
                      }
                    };
                    return (
                      <TableRow key={log.id} sx={{ verticalAlign: "top" }}>
                        <TableCell sx={{ ...tableCellStyles, minWidth: 100 }}>
                          {formatDate(log.dateOfEntry)}
                        </TableCell>
                        <TableCell sx={{ ...tableCellStyles, minWidth: 100 }}>
                          {formatTime12Hour(log.startTime)} -{" "}
                          {formatTime12Hour(log.endTime)}
                        </TableCell>
                        <TableCell sx={{ ...tableCellStyles, minWidth: 150 }}>
                          <Box>
                            {log.staffAttendances &&
                            log.staffAttendances.length > 0 ? (
                              log.staffAttendances.map((attendance, index) => {
                                const staff = attendance.staff;
                                const staffName =
                                  staff?.name || "Unknown Staff";
                                const staffType = staff?.type || "UNKNOWN";
                                const status =
                                  attendance.attendanceStatus || "UNKNOWN";

                                return (
                                  <Box
                                    key={attendance.id || index}
                                    sx={{
                                      mb: 1,
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        fontSize: tableCellStyles.fontSize,
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                      }}
                                    >
                                      {formatText(staffType)} :{" "}
                                      {formatText(staffName)} (
                                      {formatText(status)})
                                    </Typography>
                                  </Box>
                                );
                              })
                            ) : (
                              <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ fontSize: tableCellStyles.fontSize }}
                              >
                                No staff assigned
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell
                          sx={tableCellStyles}
                          style={{ maxWidth: "200px", overflow: "hidden" }}
                        >
                          <DocumentStackDisplay documents={log.documents} />
                        </TableCell>
                        <TableCell sx={tableCellStyles}>
                          <Button
                            variant="outlined"
                            color="primary"
                            onClick={handleEditLog}
                            {...smallButtonProp}
                          >
                            Edit
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </>
      )}
    </Container>
  );
};

export default ProjectLogsByVehicle;
